
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  CheckCircle, 
  ArrowRight, 
  ChevronRight, 
  Star, 
  Clock, 
  Zap, 
  Globe, 
  ShieldCheck, 
  Search, 
  Phone
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import ScrollReveal from '@/components/ui/ScrollReveal';
import { submitToPabbly } from '@/utils/pabblyConnect';

// GTM event function
const triggerGTMEvent = (event: string, data: Record<string, any> = {}) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      event,
      ...data
    });
    console.log(`GTM Event triggered: ${event}`, data);
  } else {
    console.warn('Google Tag Manager not initialized');
  }
};

const WebDesignLanding = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    projectDescription: '',
    consent: false,
    source: 'web-design-landing'
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleCheckboxChange = (checked: boolean) => {
    setFormData(prev => ({ ...prev, consent: checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.consent) {
      alert("Please agree to the terms and privacy policy to proceed.");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Submit form data to Pabbly
      await submitToPabbly(formData, 'web_design_landing_form');
      
      // Trigger GTM form submission event
      triggerGTMEvent('form_submission', {
        form_id: 'web_design_landing_form',
        form_name: 'Web Design Landing Page Form',
        form_source: formData.source
      });
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        projectDescription: '',
        consent: false,
        source: 'web-design-landing'
      });
      
      // Navigate to thank you section
      document.getElementById('thank-you')?.scrollIntoView({ behavior: 'smooth' });
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Professional Website Design Services | Astrem</title>
        <meta name="description" content="Transform your online presence with our professional website design services. Fast, responsive websites that convert visitors into customers." />
        <meta property="og:title" content="Professional Website Design Services | Astrem" />
        <meta property="og:description" content="Transform your online presence with our professional website design services. Fast, responsive websites that convert visitors into customers." />
        <meta property="og:url" content="https://astrem.co/web-design" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero Section with Gradient Background */}
      <div className="relative bg-gradient-to-br from-black to-zinc-800 text-white">
        <div className="container mx-auto px-4 py-20 md:py-32">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <ScrollReveal>
                <span className="inline-block px-4 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium border border-white/20 mb-4">
                  Limited Time Offer: 15% Off New Websites
                </span>
              </ScrollReveal>

              <ScrollReveal delay={150}>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-semibold mb-6">
                  Websites That Convert Visitors Into <span className="text-sky-400">Customers</span>
                </h1>
              </ScrollReveal>

              <ScrollReveal delay={300}>
                <p className="text-xl text-white/80 mb-8">
                  Professional, custom website design for your business. Fast, mobile-friendly, and built to convert.
                </p>
              </ScrollReveal>

              <ScrollReveal delay={450}>
                <div className="flex flex-wrap gap-4 mb-8">
                  <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white rounded-full" onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}>
                    Get Your Free Quote
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-black rounded-full">
                    <Phone className="mr-2 h-4 w-4" />
                    Call 225-331-6114
                  </Button>
                </div>
              </ScrollReveal>

              <ScrollReveal delay={600}>
                <div className="flex items-center">
                  <div className="flex -space-x-2 mr-3">
                    {[1, 2, 3].map((_, i) => (
                      <div key={i} className="w-10 h-10 rounded-full border-2 border-white bg-gray-300 overflow-hidden">
                        <img 
                          src={`https://randomuser.me/api/portraits/men/${i + 20}.jpg`} 
                          alt={`Client ${i}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="text-white">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-sm font-medium">Trusted by 100+ businesses in Louisiana</p>
                  </div>
                </div>
              </ScrollReveal>
            </div>

            <div className="order-1 md:order-2">
              <ScrollReveal>
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20">
                  <img 
                    src="/lovable-uploads/b24584a6-a744-4fa6-bb55-b42c9b3d07f0.png" 
                    alt="Website Design Preview" 
                    className="w-full rounded-lg shadow-2xl transform hover:scale-[1.02] transition-transform duration-300"
                  />
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" className="w-full h-[100px] fill-white">
            <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,42.7C1120,32,1280,32,1360,32L1440,32L1440,100L1360,100C1280,100,1120,100,960,100C800,100,640,100,480,100C320,100,160,100,80,100L0,100Z"></path>
          </svg>
        </div>
      </div>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <ScrollReveal>
              <h2 className="text-3xl md:text-4xl font-display font-semibold mb-4">
                Why Choose Astrem for Your Website?
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={150}>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                We design professional websites that not only look great but are strategically built to convert visitors into customers.
              </p>
            </ScrollReveal>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Globe className="h-8 w-8 text-sky-500" />,
                title: "Professional Design",
                description: "Custom-designed websites that reflect your brand identity and impress your visitors."
              },
              {
                icon: <Zap className="h-8 w-8 text-sky-500" />,
                title: "Lightning Fast",
                description: "Optimized for speed to improve user experience and search engine rankings."
              },
              {
                icon: <ShieldCheck className="h-8 w-8 text-sky-500" />,
                title: "Secure & Reliable",
                description: "Built with security in mind to protect your business and customer data."
              },
              {
                icon: <Search className="h-8 w-8 text-sky-500" />,
                title: "SEO Optimized",
                description: "Structured to help you rank higher in search engines and get found by customers."
              },
              {
                icon: <Clock className="h-8 w-8 text-sky-500" />,
                title: "Quick Turnaround",
                description: "Get your professional website up and running in just 2-3 weeks."
              },
              {
                icon: <CheckCircle className="h-8 w-8 text-sky-500" />,
                title: "Conversion Focused",
                description: "Designed with clear call-to-actions to convert visitors into leads and customers."
              }
            ].map((benefit, index) => (
              <ScrollReveal key={index} delay={150 * index}>
                <Card className="h-full border-gray-200 hover:border-sky-200 hover:shadow-md transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="bg-sky-50 rounded-full p-3 inline-block mb-4">
                      {benefit.icon}
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Showcase */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <ScrollReveal>
              <h2 className="text-3xl md:text-4xl font-display font-semibold mb-4">
                Our Website Portfolio
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={150}>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Take a look at some of the websites we've designed for businesses like yours.
              </p>
            </ScrollReveal>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                image: "/lovable-uploads/2060592f-90be-4612-aaee-8a55d9b15dc0.png",
                title: "Modern E-commerce Store",
                category: "Retail"
              },
              {
                image: "/lovable-uploads/908e0364-dc0e-4b62-a951-5740a58e2736.png",
                title: "Professional Services Website",
                category: "Legal"
              },
              {
                image: "/lovable-uploads/b19346aa-736e-4cf9-a17d-17475e2b916a.png",
                title: "Restaurant Website",
                category: "Food & Beverage"
              }
            ].map((project, index) => (
              <ScrollReveal key={index} delay={150 * index}>
                <div className="group overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
                  <div className="relative overflow-hidden h-64">
                    <img 
                      src={project.image} 
                      alt={project.title} 
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <Button className="bg-white text-black hover:bg-gray-200">
                        View Project
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-semibold">{project.title}</h3>
                    <p className="text-gray-500">{project.category}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              variant="outline" 
              className="border-black text-black hover:bg-black hover:text-white"
              onClick={() => navigate('/portfolio')}
            >
              View All Projects
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <ScrollReveal>
              <h2 className="text-3xl md:text-4xl font-display font-semibold mb-4">
                Affordable Website Packages
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={150}>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Choose the perfect website package for your business needs and budget.
              </p>
            </ScrollReveal>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                title: "Starter",
                price: "$1,499",
                description: "Perfect for small businesses just getting started online",
                features: [
                  "5 Page Website",
                  "Mobile Responsive Design",
                  "Contact Form",
                  "Basic SEO Setup",
                  "Google Business Profile",
                  "1 Round of Revisions"
                ],
                popular: false,
                cta: "Get Started"
              },
              {
                title: "Business",
                price: "$2,499",
                description: "Our most popular package for established businesses",
                features: [
                  "Up to 10 Pages",
                  "Premium Design",
                  "Advanced SEO Setup",
                  "Blog Integration",
                  "Social Media Integration",
                  "Google Analytics Setup",
                  "2 Rounds of Revisions",
                  "1 Hour Training Session"
                ],
                popular: true,
                cta: "Get Started"
              },
              {
                title: "E-commerce",
                price: "$3,999",
                description: "Complete solution for online stores and product sales",
                features: [
                  "Up to 15 Pages",
                  "Product Management",
                  "Payment Gateway Integration",
                  "Inventory Management",
                  "Order Tracking System",
                  "Customer Accounts",
                  "Email Marketing Integration",
                  "3 Rounds of Revisions",
                  "2 Hours Training Session"
                ],
                popular: false,
                cta: "Get Started"
              }
            ].map((plan, index) => (
              <ScrollReveal key={index} delay={150 * index}>
                <div className={`border rounded-2xl overflow-hidden transition-all duration-300 h-full flex flex-col ${
                  plan.popular 
                    ? 'border-sky-500 shadow-lg scale-105 relative z-10' 
                    : 'border-gray-200 hover:border-sky-200 hover:shadow-md'
                }`}>
                  {plan.popular && (
                    <div className="bg-sky-500 text-white text-center py-2 text-sm font-medium">
                      MOST POPULAR
                    </div>
                  )}
                  <div className="p-6 flex-grow">
                    <h3 className="text-2xl font-bold mb-2">{plan.title}</h3>
                    <div className="mb-4">
                      <span className="text-4xl font-bold">{plan.price}</span>
                      <span className="text-gray-500"> one-time</span>
                    </div>
                    <p className="text-gray-600 mb-6">{plan.description}</p>
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-sky-500 mt-0.5 mr-2 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-6 pt-0">
                    <Button 
                      className={`w-full ${
                        plan.popular
                          ? 'bg-sky-500 hover:bg-sky-600 text-white'
                          : 'bg-black hover:bg-gray-800 text-white'
                      }`}
                      onClick={() => {
                        document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
                        // Pre-select package in hidden field
                        setFormData(prev => ({ ...prev, package: plan.title }));
                      }}
                    >
                      {plan.cta}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <ScrollReveal>
              <h2 className="text-3xl md:text-4xl font-display font-semibold mb-4">
                What Our Clients Say
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={150}>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Don't take our word for it. See what our satisfied clients have to say about our website design services.
              </p>
            </ScrollReveal>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                quote: "Working with Astrem was a game-changer for our business. Our new website looks amazing and has significantly increased our leads.",
                author: "Sarah Johnson",
                company: "Johnson Law Firm",
                avatar: "https://randomuser.me/api/portraits/women/32.jpg"
              },
              {
                quote: "The Astrem team delivered our e-commerce website on time and under budget. Sales have increased by 45% since launch!",
                author: "Michael Rodriguez",
                company: "Urban Boutique",
                avatar: "https://randomuser.me/api/portraits/men/54.jpg"
              },
              {
                quote: "I was impressed by how quickly they understood our brand and translated it into a beautiful, functional website.",
                author: "Lisa Chen",
                company: "Chen Consulting Group",
                avatar: "https://randomuser.me/api/portraits/women/65.jpg"
              }
            ].map((testimonial, index) => (
              <ScrollReveal key={index} delay={150 * index}>
                <Card className="h-full border-gray-200 hover:border-sky-200 hover:shadow-md transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-gray-700 mb-6 italic">"{testimonial.quote}"</p>
                    <div className="flex items-center">
                      <div className="w-12 h-12 rounded-full overflow-hidden mr-4">
                        <img 
                          src={testimonial.avatar} 
                          alt={testimonial.author} 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <h4 className="font-semibold">{testimonial.author}</h4>
                        <p className="text-sm text-gray-500">{testimonial.company}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <ScrollReveal>
                <h2 className="text-3xl md:text-4xl font-display font-semibold mb-4">
                  Ready to Get Started?
                </h2>
              </ScrollReveal>
              <ScrollReveal delay={150}>
                <p className="text-lg text-gray-600">
                  Fill out the form below and we'll get back to you within 24 hours with a free quote.
                </p>
              </ScrollReveal>
            </div>

            <ScrollReveal delay={300}>
              <Card className="border-gray-200 shadow-lg">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="firstName" className="text-gray-700 mb-1 block">
                          First Name *
                        </Label>
                        <Input
                          id="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          className="border-gray-300 focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName" className="text-gray-700 mb-1 block">
                          Last Name *
                        </Label>
                        <Input
                          id="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          className="border-gray-300 focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="email" className="text-gray-700 mb-1 block">
                          Email Address *
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="border-gray-300 focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-gray-700 mb-1 block">
                          Phone Number
                        </Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          className="border-gray-300 focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="projectDescription" className="text-gray-700 mb-1 block">
                        Tell us about your project
                      </Label>
                      <Textarea
                        id="projectDescription"
                        rows={4}
                        value={formData.projectDescription}
                        onChange={handleChange}
                        className="border-gray-300 focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
                        placeholder="What kind of website do you need? Any specific features or requirements?"
                      />
                    </div>

                    <div className="flex items-start space-x-2">
                      <Checkbox 
                        id="consent" 
                        checked={formData.consent} 
                        onCheckedChange={handleCheckboxChange}
                        className="mt-1"
                      />
                      <label htmlFor="consent" className="text-sm text-gray-700">
                        <span dangerouslySetInnerHTML={{ __html: `By checking this box, you agree to Astrem's <a class="text-sky-600 hover:underline" href="/terms" target="_blank">Terms of Service</a> and <a class="text-sky-600 hover:underline" href="/privacy-policy" target="_blank">Privacy Policy</a>. You also consent to receive text messages from Astrem. Message frequency may vary but is generally kept to a minimum. Standard messages and data rates may apply.` }} />
                      </label>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-sky-500 hover:bg-sky-600 text-white py-3 text-lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Submitting..." : "Get Your Free Quote"}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Thank You Section (Hidden until form submission) */}
      <section id="thank-you" className="py-20 bg-gray-50 hidden">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-2xl mx-auto">
            <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-3xl font-display font-semibold mb-4">Thank You for Your Submission!</h2>
            <p className="text-lg text-gray-600 mb-8">
              We've received your information and will be in touch within 24 hours with your free quote.
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="bg-black hover:bg-gray-800 text-white"
            >
              Return to Homepage
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <ScrollReveal>
              <h2 className="text-3xl md:text-4xl font-display font-semibold mb-4">
                Frequently Asked Questions
              </h2>
            </ScrollReveal>
          </div>

          <div className="max-w-3xl mx-auto">
            {[
              {
                question: "How long does it take to build a website?",
                answer: "Our typical timeline for a standard business website is 2-3 weeks from start to finish. E-commerce sites may take 4-6 weeks depending on complexity and the number of products."
              },
              {
                question: "Do you offer website maintenance?",
                answer: "Yes, we offer monthly maintenance packages to keep your website secure, updated, and running smoothly. This includes regular updates, security monitoring, and content changes."
              },
              {
                question: "Will my website be mobile-friendly?",
                answer: "Absolutely! All our websites are built with responsive design, ensuring they look great and function properly on all devices including desktops, tablets, and smartphones."
              },
              {
                question: "Can I update the website myself?",
                answer: "Yes, we build our websites on user-friendly content management systems that allow you to make updates yourself. We also provide training to help you learn how to manage your content."
              },
              {
                question: "What's included in your SEO setup?",
                answer: "Our basic SEO setup includes optimized page titles, meta descriptions, heading structure, image optimization, schema markup, and Google Business Profile setup to help improve your visibility in search results."
              }
            ].map((faq, index) => (
              <ScrollReveal key={index} delay={150 * index}>
                <div className="mb-6 border-b border-gray-200 pb-6">
                  <h3 className="text-xl font-semibold mb-2">{faq.question}</h3>
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>

          <div className="text-center mt-12">
            <ScrollReveal delay={750}>
              <p className="text-lg mb-6">Still have questions?</p>
              <Button 
                onClick={() => navigate('/contact')}
                variant="outline"
                className="border-black text-black hover:bg-black hover:text-white"
              >
                Contact Us
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-black to-zinc-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-semibold mb-6">
              Ready to Transform Your Online Presence?
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={150}>
            <p className="text-xl text-white/80 mb-8 max-w-3xl mx-auto">
              Get started today with a free consultation and quote for your new website.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={300}>
            <div className="flex flex-wrap justify-center gap-4">
              <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white rounded-full" onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}>
                Get Your Free Quote
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-black rounded-full" onClick={() => navigate('/portfolio')}>
                View Our Portfolio
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Custom JavaScript to show thank you section after form submission */}
      <script dangerouslySetInnerHTML={{
        __html: `
        document.addEventListener("DOMContentLoaded", function() {
          // Check URL parameters for form submission status
          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.get('submitted') === 'true') {
            const thankYouSection = document.getElementById('thank-you');
            if (thankYouSection) {
              thankYouSection.classList.remove('hidden');
              thankYouSection.scrollIntoView({ behavior: 'smooth' });
            }
          }
        });
        `
      }} />
    </>
  );
};

export default WebDesignLanding;
